import {
  ApiKeyCreateBodySchema,
  ApiKeyCreatedResponseSchema,
  ApiKeyListResponseSchema,
  ServiceLogPage,
  type ApiKey,
  type ApiKeyCreatedResponse,
  type ApiKeyScope,
  type GetAutosendResult,
  type UpdateAutosendInput,
  type AutosendSettingsType,
  type ServiceLogSeverityType,
  type ServiceLogCategoryType,
  type ServiceLogSourceType,
  type ServiceLogPageType,
} from '../schemas/settings.schema'
import { apiFetch, readApiJson } from '@/lib/api-fetch'
import {
  StoreManagementSettingsSchema,
  type StoreManagementSettings,
} from '../schemas/store-management.schema'

export async function getAutosendSettings(): Promise<GetAutosendResult> {
  const res = await apiFetch('/api/settings/autosend')
  return await readApiJson<GetAutosendResult>(res)
}

export type ServiceLogFilters = {
  severity?: ServiceLogSeverityType
  category?: ServiceLogCategoryType
  source?: ServiceLogSourceType
}

export async function getServiceLogs(filters: ServiceLogFilters, cursor?: string): Promise<ServiceLogPageType> {
  const params = new URLSearchParams({ limit: '25' })
  if (filters.severity) params.set('severity', filters.severity)
  if (filters.category) params.set('category', filters.category)
  if (filters.source) params.set('source', filters.source)
  if (cursor) params.set('cursor', cursor)
  const response = await apiFetch(`/api/settings/service-logs?${params}`)
  if (!response.ok) throw new Error('Не удалось загрузить журнал сервисов.')
  try {
    return ServiceLogPage.parse(await response.json())
  } catch {
    throw new Error('Не удалось загрузить журнал сервисов.')
  }
}

export async function updateAutosendSettings(
  patch: UpdateAutosendInput
): Promise<{ ok: true; settings: AutosendSettingsType }> {
  const res = await apiFetch('/api/settings/autosend', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  return await readApiJson<{ ok: true; settings: AutosendSettingsType }>(res)
}

export async function getApiKeys(signal?: AbortSignal): Promise<readonly ApiKey[]> {
  const response = await apiFetch('/api/settings/api-keys', { signal })
  return ApiKeyListResponseSchema.parse(await readApiJson<unknown>(response)).keys
}

export async function createApiKey(name: string, scope: ApiKeyScope = 'owner'): Promise<ApiKeyCreatedResponse> {
  const body = ApiKeyCreateBodySchema.parse({ name, scope })
  const response = await apiFetch('/api/settings/api-keys', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return ApiKeyCreatedResponseSchema.parse(await readApiJson<unknown>(response))
}

export async function revokeApiKey(keyId: string): Promise<ApiKey> {
  const response = await apiFetch(`/api/settings/api-keys/${encodeURIComponent(keyId)}/revoke`, {
    method: 'POST',
  })
  return ApiKeyCreatedResponseSchema.shape.key.parse(await readApiJson<unknown>(response))
}

export async function getStoreManagementSettings(): Promise<StoreManagementSettings> {
  const response = await apiFetch('/api/settings/store-management')
  return StoreManagementSettingsSchema.parse(await readApiJson<unknown>(response))
}

export async function updateStoreManagementSettings(
  settings: StoreManagementSettings,
): Promise<StoreManagementSettings> {
  const response = await apiFetch('/api/settings/store-management', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  })
  return StoreManagementSettingsSchema.parse(await readApiJson<unknown>(response))
}
